import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import moment from "moment";
import excelJs from "exceljs";
import UserListToolbar from "./UserListToolbar";
import UserListHead from "./UserListHead";
import SearchNotFound from "../../../SearchNotFound";
import { getRenewalPostDoc } from "../../../../core/service/renewalPostDoc.service";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { mentorConfirm } from "../../../../core/service/renewalPostDoc.service";
import Swal from "sweetalert2";
import Page from "../../../Page";

import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  List,
  ListItem,
  Radio,
  FormControl,
  FormControlLabel,
  RadioGroup,
  TextField,
  Container,
  Link,
} from "@mui/material";
import { config } from "../../../../core/service/config.service";

const isValidDate = (d) => {
  return d instanceof Date && !isNaN(d);
};

const TABLE_HEAD = [
  { id: "name", label: "Name", alignRight: false },
  { id: "faculty", label: "Faculty", alignRight: false },
  { id: "year", label: "Year", alignRight: false },
];

const ROLE_ADMIN = "admin";
const ROLE_MENTOR = "mentor";
const ROLE_POSTDOC_3Y = "post_doc_3y";
const ROLE_POSTDOC_5Y = "post_doc_5y";
const ROLE_PHD = "PhD";

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const applySortFilter = (array, comparator, query) => {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return array.filter((e) => {
      const { title_en, name_en, middle_en, surname_en } = e;
      const name = `${title_en} ${name_en} ${middle_en || ""} ${surname_en}`;
      return name.toLowerCase().indexOf(query.toLowerCase()) !== -1;
    });
  }
  return stabilizedThis.map((el) => el[0]);
};

RenewalFellowshipTable.propTypes = {
  renewalList: PropTypes.array,
};

export default function RenewalFellowshipTable({ renewalList }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { user: currentUser } = useSelector((state) => state.authReducer);
  const [openDetailRenewal, setOpenDetailRenewal] = useState(false);
  const [moreDetailListRenewal, setMoreDetailListRenewal] = useState({});

  const openDetail = async (row) => {
    const { id } = row;
    const moreDetail = await getRenewalPostDoc(id);
    setMoreDetailListRenewal(moreDetail);
    setOpenDetailRenewal(true);
  };

  const closeMoreDetail = () => {
    setFieldValue("mentor_renew_postdoctoral", "");
    setFieldValue("mentor_start_new_contract", "");
    setFieldValue("mentor_note", "");
    setOpenDetailRenewal(false);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - renewalList.length) : 0;

  const filteredUsers = applySortFilter(
    renewalList,
    getComparator(order, orderBy),
    filterName
  );

  const isUserNotFound = filteredUsers.length === 0;

  const renewalFellowshipSchema = Yup.object().shape({
    mentor_renew_postdoctoral: Yup.string().required("is required"),
    mentor_start_new_contract: Yup.string().when("mentor_renew_postdoctoral", {
      is: (coAuthor) => coAuthor === "yes",
      then: Yup.string().required(`Start new contract is required`),
    }),
    mentor_note: Yup.string().required("Note is required"),
  });

  const formik = useFormik({
    initialValues: {
      mentor_renew_postdoctoral: "",
      mentor_start_new_contract: "",
      mentor_note: "",
    },
    validationSchema: renewalFellowshipSchema,
    onSubmit: async (values) => {
      var formData = new FormData();
      for (const [key, value] of Object.entries(values)) {
        formData.append(key, value);
      }
      formData.append("mentor_approve", 1);
      await mentorConfirm(moreDetailListRenewal?.id, formData)
        .then(() => closeMoreDetail())
        .catch(() =>
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Something went wrong",
          })
        );
    },
  });

  const {
    errors,
    getFieldProps,
    handleChange,
    handleSubmit,
    touched,
    setFieldValue,
    values,
  } = formik;

  const exportExcel = async () => {
    const workbook = new excelJs.Workbook();
    const ws = workbook.addWorksheet(`Report`);
    ws.columns = [
      {
        header: "Name",
        key: "name_en",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Faculty",
        key: "faculty",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Year",
        key: "year",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
    ];

    ws.getRow(1).height = 30;

    await Promise.all(
      filteredUsers.map(async (val, index) => {
        ws.addRow([
          `${val.name_en} ${val.middle_en || ""} ${val.surname_en}`,
          val.faculty,
          moment(val.created_date).format("DD/MM/YY"),
        ]);
        ws.getRow(index + 2).font = {
          ...ws.getRow(index + 2).font,
          bold: false,
        };
      })
    );

    workbook.xlsx
      .writeBuffer({
        base64: true,
      })
      .then((xls64) => {
        // build anchor tag and attach file (works in chrome)
        var a = document.createElement("a");
        var data = new Blob([xls64], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        var url = URL.createObjectURL(data);
        a.href = url;
        a.download = `RENEWAL_OF_FELLOWSHIP_${moment().format(
          "YYYYMMDD_HHmmss"
        )}.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout(function () {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 0);
      })
      .catch(function (error) {
        console.log(error.message);
      });
  };

  return (
    <Page title="Renewal of Fellowship">
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Renewal of Fellowship
          </Typography>
          <Link
            href={`${config.host_api}export/renewal-fellowship/${currentUser.id}`}
          >
            <Button variant="contained">Export excel</Button>
          </Link>
        </Stack>

        <Card>
          <UserListToolbar
            filterName={filterName}
            onFilterName={handleFilterByName}
          />

          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={renewalList.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const {
                      id,
                      faculty,
                      name_en,
                      surname_en,
                      middle_en,
                      created_date,
                    } = row;

                    return (
                      <TableRow tabIndex={-1} hover key={id}>
                        <TableCell />

                        <TableCell component="th" scope="row" padding="none">
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography>
                              {`${name_en} ${middle_en} ${surname_en}` || ""}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">{faculty}</TableCell>

                        <TableCell align="left">
                          {moment(created_date).format("DD/MM/YY")}
                        </TableCell>

                        <TableCell align="left">
                          <IconButton onClick={() => openDetail(row)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>

                        <Dialog
                          open={openDetailRenewal}
                          onClose={closeMoreDetail}
                          fullWidth={true}
                          maxWidth={"lg"}
                        >
                          <DialogContent>
                            <Stack alignItems="center">
                              <Typography variant="h4">
                                Renewal of Fellowship
                              </Typography>
                            </Stack>
                            <Grid container spacing={2}>
                              <Grid item xs>
                                <List>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Name:
                                      </Typography>
                                      <Typography>{`${moreDetailListRenewal.name_en} ${moreDetailListRenewal.middle_en} ${moreDetailListRenewal.surname_en}`}</Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Email:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListRenewal.email}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Year:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListRenewal?.year_contract}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Start the new contract:
                                      </Typography>
                                      <Typography>
                                        {
                                          moreDetailListRenewal.start_newContract
                                        }
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Request Date:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListRenewal.created_date}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Note:
                                      </Typography>
                                      <Typography>
                                        {
                                          moreDetailListRenewal?.additional_inform_note
                                        }
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                </List>
                              </Grid>
                            </Grid>

                            <FormikProvider value={formik}>
                              <Form
                                autoComplete="off"
                                noValidate
                                onSubmit={handleSubmit}
                              >
                                <Grid
                                  container
                                  direction="column"
                                  justifyContent="center"
                                  alignItems="center"
                                  sx={{ marginTop: 3 }}
                                >
                                  <FormControl>
                                    <Typography
                                      variant="subtitle1"
                                      gutterBottom
                                      component="span"
                                    >
                                      Would you like to renew the C2F
                                      postdoctoral fellowship contract with this
                                      researcher?
                                    </Typography>
                                    <RadioGroup
                                      {...getFieldProps(
                                        "mentor_renew_postdoctoral"
                                      )}
                                    >
                                      <Stack
                                        direction="row"
                                        justifyContent="center"
                                        alignItems="center"
                                        spacing={2}
                                      >
                                        <FormControlLabel
                                          value="yes"
                                          control={<Radio />}
                                          label="Yes, I would."
                                        />
                                        <FormControlLabel
                                          value="no"
                                          control={<Radio />}
                                          label="No, I would not."
                                        />
                                      </Stack>
                                      <span
                                        style={{
                                          color: "#FF4842",
                                          lineHeight: 1.5,
                                          fontSize: "0.75rem",
                                          fontFamily: "sans-serif",
                                          fontWeight: 400,
                                          textAlign: "left",
                                          marginTop: "3px",
                                          marginRight: "14px",
                                          marginBottom: 0,
                                          marginLeft: "14px",
                                        }}
                                      >
                                        {errors.mentor_renew_postdoctoral}
                                      </span>
                                    </RadioGroup>
                                  </FormControl>

                                  <FormControl fullWidth>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                      component="span"
                                    >
                                      Additional information/note
                                    </Typography>
                                    <TextField
                                      variant="outlined"
                                      onChange={(e) => handleChange(e)}
                                      {...getFieldProps("mentor_note")}
                                      error={Boolean(
                                        touched.mentor_note &&
                                          errors.mentor_note
                                      )}
                                      helperText={
                                        touched.mentor_note &&
                                        errors.mentor_note
                                      }
                                    />
                                  </FormControl>

                                  {values.mentor_renew_postdoctoral ===
                                    "yes" && (
                                    <FormControl fullWidth>
                                      <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                        component="span"
                                      >
                                        When would you like to start the new
                                        contract? *
                                      </Typography>

                                      <LocalizationProvider
                                        dateAdapter={AdapterDateFns}
                                      >
                                        <DatePicker
                                          inputFormat="dd/MM/yyyy"
                                          value={
                                            values.mentor_start_new_contract
                                          }
                                          fullWidth
                                          onChange={(date) => {
                                            if (isValidDate(date)) {
                                              setFieldValue(
                                                "mentor_start_new_contract",
                                                date.toISOString()
                                              );
                                            } else {
                                              setFieldValue(
                                                "mentor_start_new_contract",
                                                date
                                              );
                                            }
                                          }}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              variant="outlined"
                                              {...getFieldProps(
                                                "mentor_start_new_contract"
                                              )}
                                              error={Boolean(
                                                touched.mentor_start_new_contract &&
                                                  errors.mentor_start_new_contract
                                              )}
                                              helperText={
                                                touched.mentor_start_new_contract &&
                                                errors.mentor_start_new_contract
                                              }
                                            />
                                          )}
                                        />
                                      </LocalizationProvider>
                                    </FormControl>
                                  )}
                                </Grid>

                                <DialogActions>
                                  {moreDetailListRenewal.mentor_renew_postdoctoral ? null : (
                                    <Button type="submit">Confirm</Button>
                                  )}
                                </DialogActions>
                              </Form>
                            </FormikProvider>
                          </DialogContent>
                        </Dialog>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
              {isUserNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={renewalList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
